"use client";

import clsx from "clsx";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { Button } from "@/components/common";
import { BTN_TYPES } from "@/utils/constants";
import useCart from "@/cart/useCart";

export default function AddToCartButton({
  children,
  btnType = BTN_TYPES.PRIMARY,
  productId,
  quantity = 1,
  className = "",
  quantityAvailable,
  availableForSale,
  redirectToCart = false,
  gtmButtonName = "",
  showQuantityAndCartIcon = false
}) {
  const router = useRouter();
  const { cart, addItem } = useCart();
  const cartQuantity = cart?.totalQuantity || 0;
  const items = cart?.lines?.edges?.map(({ node }) => node) || [];
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [quantityAndCartIcon, setQuantityAndCartIcon] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const cartItem = items.find(item => item.merchandise.id === productId);

  const [currentQuantity, setCurrentQuantity] = useState(
    cartItem?.quantity || quantity
  );

  const goToCartCheckout = () => {
    if (cartQuantity === 0) return;
    router.push("/cart");
  };

  useEffect(() => {
    let timer;

    if (showSuccess) {
      timer = setTimeout(() => {
        setShowSuccess(false);
        setQuantityAndCartIcon(true);
      }, 2500);
    }

    return () => clearTimeout(timer);
  }, [showSuccess]);

  useEffect(() => {
    if (cartItem) {
      setCurrentQuantity(cartItem.quantity);
    } else {
      setCurrentQuantity(quantity);
    }
  }, [cartItem, quantity]);

  const handleAddToCart = async () => {
    if (!availableForSale) return;
    setIsLoading(true);
    if (redirectToCart) {
      router.push("/cart");
    }
    if (gtmButtonName) {
      window.dataLayer?.push({
        event: "reserve_now",
        value: gtmButtonName,
      });
    }
    try {
      await addItem(productId, quantity);
      if (!redirectToCart) {
        setShowSuccess(true);
      }
    } catch (error) {
      console.error("Error adding to cart:", error);
      if (redirectToCart) {
        router.back();
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (showSuccess) {
    return (
      <div
        className={clsx(
          className,
          "flex pl-[22px] pr-[21px] gap-[18px] text-[18px] items-center text-center btn-secondary hover:bg-white hover:text-primary cursor-auto bg-white z-1 relative"
        )}
      >
        <svg
          width="19"
          height="14"
          viewBox="0 0 21 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M1 8.28L7.16216 15L20 1" stroke="#1F2022" />
        </svg>
        <span className="uppercase font-medium">Added to Cart!</span>
      </div>
    );
  }

  if (cartItem?.quantity > 0 && !redirectToCart && quantityAndCartIcon && showQuantityAndCartIcon) {
    return (
      <button
        className="px-[32px] font-medium text-[18px] z-1 h-[60px] flex gap-[12px] md:gap-[23px] items-center text-center btn-secondary hover:bg-white hover:text-primary cursor-pointer bg-white relative"
        onClick={goToCartCheckout}
      >
        {currentQuantity}
        <svg width="24" height="20" viewBox="0 0 24 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20.3515 19.5H6.69091L5.8351 14.1977M5.8351 14.1977H21.0485L23 3.59302H4.12347M5.8351 14.1977L4.12347 3.59302M4.12347 3.59302L3.62424 0.5H0" stroke="#1F2022" />
        </svg>
      </button>
    );
  }

  const mouseEnter = () => {
    setIsHovered(true);
  };

  const mouseLeave = () => {
    setIsHovered(false);
  };

  return (
    <Button
      onClickFn={handleAddToCart}
      btnType={btnType}
      disabled={isLoading || !availableForSale}
      customClasses={className}
      onMouseEnter={mouseEnter}
      onMouseLeave={mouseLeave}
    >
      {availableForSale ? (
        showQuantityAndCartIcon && !isLoading ? (
          <span className="group flex items-center gap-[16px]">
            <span className="md:group-hover:hidden hidden md:block text-white text-lg uppercase font-medium">
              <svg width="19" height="19" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8.81373 19.5V11H0.5V8.39463H8.81373V0.5H11.5V8.39463H19.5V11H11.5V19.5H8.81373Z" fill="white" />
              </svg>
            </span>
            <span className="md:group-hover:block md:hidden px-[1px] md:px-[2px]">{children}</span>
          </span>
        ) : (
          children
        )
      ) : (
        <span className="text-disable uppercase font-medium">Out of stock</span>
      )}

    </Button>
  );
}